import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { useCart } from "@/context/CartContext";

const FIELDS = [
  { key: "name", label: "NAME", placeholder: "Your name", testId: "checkout-name-input" },
  { key: "email", label: "EMAIL", type: "email", placeholder: "your@email.com", testId: "checkout-email-input" },
  { key: "address", label: "ADDRESS", placeholder: "Street and number", testId: "checkout-address-input" },
  { key: "city", label: "CITY", placeholder: "City", testId: "checkout-city-input" },
];

const inputClass = "w-full bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red";

const payLabel = (sending, total) => {
  if (sending) return "TAKING YOU TO PAYMENT…";
  if (total !== null) return `PAY — $${total.toFixed(2)}`;
  return "PAY";
};

const shippingLabel = (quoting, shipping) => {
  if (quoting) return "…";
  if (shipping !== null) return `$${shipping.toFixed(2)}`;
  return "Enter address";
};

function AddressFields({ form, setForm, isUS }) {
  return (
    <>
      {FIELDS.map((f) => (
        <div key={f.key}>
          <label className="font-college text-base block mb-1.5" htmlFor={f.testId}>{f.label}</label>
          <input
            id={f.testId}
            data-testid={f.testId}
            type={f.type || "text"}
            required
            value={form[f.key]}
            onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            placeholder={f.placeholder}
            className={inputClass}
          />
        </div>
      ))}
      <div>
        <label className="font-college text-base block mb-1.5" htmlFor="checkout-country-select">COUNTRY</label>
        <select
          id="checkout-country-select"
          data-testid="checkout-country-select"
          required
          value={form.country}
          onChange={(e) => setForm((p) => ({ ...p, country: e.target.value, state: "" }))}
          className={inputClass}
        >
          {COUNTRIES.map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-college text-base block mb-1.5" htmlFor="checkout-state-input">
            {isUS ? "STATE" : "STATE / REGION (OPTIONAL)"}
          </label>
          <input
            id="checkout-state-input"
            data-testid="checkout-state-input"
            required={isUS}
            value={form.state}
            onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
            placeholder={isUS ? "2 letters, e.g. NV" : "Region"}
            className={inputClass}
          />
        </div>
        <div>
          <label className="font-college text-base block mb-1.5" htmlFor="checkout-zip-input">{isUS ? "ZIP" : "POSTAL CODE"}</label>
          <input
            id="checkout-zip-input"
            data-testid="checkout-zip-input"
            required
            value={form.zip}
            onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
            placeholder={isUS ? "Zip code" : "Postal code"}
            className={inputClass}
          />
        </div>
      </div>
    </>
  );
}

function OrderSummary({ items, subtotal, shipping, quoting, total }) {
  return (
    <div>
      <div className="bg-white shadow-print" data-testid="order-summary">
        <p className="font-college text-xl px-5 py-3 border-b border-ink/15 bg-paper">YOUR PIECES</p>
        {items.map((i) => (
          <div key={i.key} className="flex gap-4 items-center px-5 py-3 border-b border-ink/20">
            <div className="w-14 h-14 shrink-0">
              <img src={i.image} alt="" className="w-full h-full object-contain p-1" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-college text-navy text-sm leading-tight truncate">{i.name}</p>
              <p className="text-xs text-ink/50">{i.size} × {i.quantity}</p>
            </div>
            <p className="font-semibold text-sm">${i.price * i.quantity}</p>
          </div>
        ))}
        <div className="px-5 py-3 space-y-1 text-sm">
          <div className="flex justify-between text-ink/70"><span>Subtotal</span><span data-testid="summary-subtotal">${subtotal}</span></div>
          <div className="flex justify-between text-ink/70">
            <span>Shipping</span>
            <span data-testid="summary-shipping">{shippingLabel(quoting, shipping)}</span>
          </div>
          <div className="flex justify-between font-bold text-base pt-1 border-t border-ink/20">
            <span>Total</span>
            <span data-testid="summary-total">{total !== null ? `$${total.toFixed(2)}` : "—"}</span>
          </div>
        </div>
      </div>
      <p className="text-xs text-ink/50 mt-4">Card payment handled by Stripe. Tax is calculated at payment. Ships worldwide — rates calculated for your address.</p>
    </div>
  );
}

export default function Checkout() {
  const { items, subtotal } = useCart();
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", state: "", zip: "", country: "US" });
  const [sending, setSending] = useState(false);
  const [shipping, setShipping] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const quoteTimer = useRef(null);

  const isUS = form.country === "US";
  const addressReady =
    form.address.trim() && form.city.trim() && form.zip.trim() && form.country && (!isUS || form.state.trim().length === 2);

  useEffect(() => {
    setShipping(null);
    if (!addressReady || items.length === 0) return;
    clearTimeout(quoteTimer.current);
    setQuoting(true);
    quoteTimer.current = setTimeout(async () => {
      try {
        const res = await api.shippingQuote({
          customer: { ...form, name: form.name || "Quote", email: form.email || "quote@runrs.shop" },
          items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity })),
        });
        setShipping(res.shipping);
      } catch (err) {
        setShipping(null);
        const msg = err.response?.data?.detail;
        if (msg) toast.error(msg);
      } finally {
        setQuoting(false);
      }
    }, 700);
    return () => clearTimeout(quoteTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.address, form.city, form.state, form.zip, form.country, items]);

  const total = shipping !== null ? subtotal + shipping : null;

  const placeOrder = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.createCheckout({
        customer: form,
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start payment. Try again.");
      setSending(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-24 text-center" data-testid="checkout-empty">
        <p className="font-college text-3xl">CART'S EMPTY.</p>
        <Link to="/shop" className="inline-block mt-6 bg-navy text-paper font-college text-xl px-8 py-3 shadow-print hover:bg-red transition-colors">
          GO PICK SOMETHING
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 pt-14" data-testid="checkout-page">
      <h1 className="font-college text-4xl sm:text-5xl text-navy" data-testid="checkout-heading">CHECKOUT</h1>
      <p className="font-college text-base text-red mt-2">SECURE CARD PAYMENT — POWERED BY STRIPE</p>

      <form onSubmit={placeOrder} className="grid gap-10 lg:grid-cols-2 mt-10 pb-10">
        <div className="space-y-4">
          <AddressFields form={form} setForm={setForm} isUS={isUS} />
          <button
            data-testid="place-order-button"
            type="submit"
            disabled={sending}
            className="w-full bg-red text-paper font-college text-2xl py-3.5 shadow-print hover:bg-navy transition-colors disabled:opacity-60 mt-2"
          >
            {payLabel(sending, total)}
          </button>
        </div>

        <OrderSummary items={items} subtotal={subtotal} shipping={shipping} quoting={quoting} total={total} />
      </form>
    </div>
  );
}
